import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { 
  Building2, Search, Filter, Download, ArrowUpDown, ChevronLeft, ChevronRight, 
  Archive, Copy, RotateCcw, MoreHorizontal, Eye, EyeOff,
  LayoutDashboard, Upload, List, ClipboardCheck, FileBarChart, CreditCard, Shield, LogOut, Menu, X,
  ArrowLeftRight, AlertTriangle, Calendar
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const CATEGORIES = [
  "Income", "Direct Expenses", "Indirect Expenses", "Internal Transfer", "VAT Payments",
  "Loan Repayments", "Owner Drawings", "Wages", "Marketing", "Software Subscriptions",
  "Rent", "Utilities", "Insurance", "Professional Fees", "Travel", "Other"
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active Only", icon: Eye },
  { value: "all", label: "All Transactions", icon: List },
  { value: "duplicate", label: "Duplicates", icon: Copy },
  { value: "archived", label: "Archived", icon: Archive },
];

export default function TransactionsPage() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [page, setPage] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [processingAction, setProcessingAction] = useState(false);
  const [statusCounts, setStatusCounts] = useState({ active: 0, duplicate: 0, archived: 0, total: 0 });
  const limit = 50;

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
    fetchStatusCounts();
  }, [selectedAccount, selectedCategory, statusFilter, page]);

  const fetchStatusCounts = async () => {
    try {
      // Fetch counts for each status
      const [activeRes, dupRes, archivedRes, allRes] = await Promise.all([
        axios.get(`${API}/transactions?status=active&limit=1000`, { headers, withCredentials: true }),
        axios.get(`${API}/transactions?status=duplicate&limit=1000`, { headers, withCredentials: true }),
        axios.get(`${API}/transactions?status=archived&limit=1000`, { headers, withCredentials: true }),
        axios.get(`${API}/transactions?status=all&limit=1000`, { headers, withCredentials: true })
      ]);
      setStatusCounts({
        active: activeRes.data.length,
        duplicate: dupRes.data.length,
        archived: archivedRes.data.length,
        total: allRes.data.length
      });
    } catch (error) {
      console.error("Failed to fetch status counts:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedAccount !== "all") params.append("account_id", selectedAccount);
      if (selectedCategory !== "all") params.append("category", selectedCategory);
      params.append("status", statusFilter);
      params.append("limit", limit.toString());
      params.append("skip", (page * limit).toString());

      const [txRes, accRes] = await Promise.all([
        axios.get(`${API}/transactions?${params.toString()}`, { headers, withCredentials: true }),
        axios.get(`${API}/accounts`, { headers, withCredentials: true })
      ]);

      setTransactions(txRes.data);
      setAccounts(accRes.data);
    } catch (error) {
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getAccountName = (accountId) => {
    const account = accounts.find(a => a.account_id === accountId);
    return account?.name || 'Unknown';
  };

  const filteredTransactions = transactions.filter(tx =>
    tx.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredTransactions.map(tx => tx.transaction_id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (txId, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, txId]);
    } else {
      setSelectedIds(prev => prev.filter(id => id !== txId));
    }
  };

  // Individual transaction actions
  const handleMarkDuplicate = async (txId) => {
    setProcessingAction(true);
    try {
      await axios.post(`${API}/transactions/${txId}/duplicate-action`, 
        { action: "mark_duplicate", reason: "Manually marked as duplicate" },
        { headers, withCredentials: true }
      );
      toast.success("Transaction marked as duplicate");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as duplicate");
    } finally {
      setProcessingAction(false);
    }
  };

  const handleArchive = async (txId) => {
    setProcessingAction(true);
    try {
      await axios.post(`${API}/transactions/${txId}/duplicate-action`, 
        { action: "archive", reason: "Manually archived" },
        { headers, withCredentials: true }
      );
      toast.success("Transaction archived");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive");
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRestore = async (txId) => {
    setProcessingAction(true);
    try {
      await axios.post(`${API}/transactions/${txId}/duplicate-action`, 
        { action: "restore" },
        { headers, withCredentials: true }
      );
      toast.success("Transaction restored");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore");
    } finally {
      setProcessingAction(false);
    }
  };

  // Bulk actions
  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) return;
    
    const actionLabels = {
      mark_duplicate: "mark as duplicate",
      archive: "archive",
      restore: "restore"
    };
    
    if (!window.confirm(`Are you sure you want to ${actionLabels[action]} ${selectedIds.length} transaction(s)?`)) {
      return;
    }

    setProcessingAction(true);
    try {
      await axios.post(`${API}/transactions/bulk-action`, 
        { transaction_ids: selectedIds, action, reason: `Bulk ${action}` },
        { headers, withCredentials: true }
      );
      toast.success(`${selectedIds.length} transaction(s) ${action === 'mark_duplicate' ? 'marked as duplicate' : action === 'archive' ? 'archived' : 'restored'}`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${actionLabels[action]} transactions`);
    } finally {
      setProcessingAction(false);
    }
  };

  const getStatusBadge = (tx) => {
    if (tx.is_duplicate) {
      return <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">Duplicate</Badge>;
    }
    if (tx.is_archived) {
      return <Badge variant="outline" className="bg-zinc-500/10 text-zinc-400 border-zinc-500/20 text-xs">Archived</Badge>;
    }
    return null;
  };

  const NavLink = ({ to, icon: Icon, children, active }) => (
    <Link to={to} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${active ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'}`}>
      <Icon className="w-5 h-5" />
      <span className="text-sm font-medium">{children}</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#121214] border-r border-zinc-800 hidden lg:flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#1C1917]" />
            </div>
            <div>
              <h1 className="font-semibold text-white">BankFlow Control</h1>
              <p className="text-xs text-zinc-500">Financial Analysis</p>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
          <NavLink to="/upload" icon={Upload}>Upload Statements</NavLink>
          <NavLink to="/transactions" icon={List} active>Transactions</NavLink>
          <NavLink to="/transfers" icon={ArrowLeftRight}>Transfer Detection</NavLink>
          <NavLink to="/reconciliation" icon={Calendar}>Weekly Summary</NavLink>
          <NavLink to="/review" icon={ClipboardCheck}>Review Queue</NavLink>
          <NavLink to="/reports" icon={FileBarChart}>Reports</NavLink>
          <NavLink to="/accounts" icon={CreditCard}>Bank Accounts</NavLink>
          {user?.role === 'admin' && <NavLink to="/admin" icon={Shield}>Admin Settings</NavLink>}
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-medium text-white">{user?.name?.charAt(0) || 'U'}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#121214] border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#1C1917]" />
            </div>
            <span className="font-semibold text-white">BankFlow</span>
          </Link>
          <Button variant="ghost" size="icon" className="text-zinc-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
        {mobileMenuOpen && (
          <nav className="absolute top-16 left-0 right-0 bg-[#121214] border-b border-zinc-800 p-4 space-y-1">
            <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
            <NavLink to="/upload" icon={Upload}>Upload</NavLink>
            <NavLink to="/transactions" icon={List} active>Transactions</NavLink>
            <NavLink to="/review" icon={ClipboardCheck}>Review</NavLink>
            <NavLink to="/reports" icon={FileBarChart}>Reports</NavLink>
            <NavLink to="/accounts" icon={CreditCard}>Accounts</NavLink>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Transactions</h1>
              <p className="text-zinc-400 text-sm mt-1">View and manage all imported transactions</p>
            </div>
          </div>

          {/* Data Integrity Notice */}
          <Card className="bg-blue-500/5 border-blue-500/20 mb-6">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-blue-300 font-medium">Data Integrity Protection</p>
                  <p className="text-xs text-blue-400/70 mt-1">
                    Transactions cannot be deleted to maintain audit integrity. Use "Mark as Duplicate" or "Archive" to exclude records from reports.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status Counts Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="status-counts-bar">
            <button
              onClick={() => { setStatusFilter("active"); setPage(0); setSelectedIds([]); }}
              className={`p-3 rounded-lg border transition-all ${
                statusFilter === 'active' 
                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                  : 'bg-[#121214] border-zinc-800 hover:border-zinc-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-xs font-medium ${statusFilter === 'active' ? 'text-emerald-400' : 'text-zinc-400'}`}>Active</span>
                <Eye className={`w-4 h-4 ${statusFilter === 'active' ? 'text-emerald-400' : 'text-zinc-600'}`} />
              </div>
              <p className={`text-2xl font-bold mt-1 ${statusFilter === 'active' ? 'text-emerald-400' : 'text-white'}`}>
                {statusCounts.active}
              </p>
            </button>
            
            <button
              onClick={() => { setStatusFilter("duplicate"); setPage(0); setSelectedIds([]); }}
              className={`p-3 rounded-lg border transition-all ${
                statusFilter === 'duplicate' 
                  ? 'bg-amber-500/10 border-amber-500/30' 
                  : 'bg-[#121214] border-zinc-800 hover:border-zinc-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-xs font-medium ${statusFilter === 'duplicate' ? 'text-amber-400' : 'text-zinc-400'}`}>Duplicates</span>
                <Copy className={`w-4 h-4 ${statusFilter === 'duplicate' ? 'text-amber-400' : 'text-zinc-600'}`} />
              </div>
              <p className={`text-2xl font-bold mt-1 ${statusFilter === 'duplicate' ? 'text-amber-400' : 'text-white'}`}>
                {statusCounts.duplicate}
              </p>
            </button>
            
            <button
              onClick={() => { setStatusFilter("archived"); setPage(0); setSelectedIds([]); }}
              className={`p-3 rounded-lg border transition-all ${
                statusFilter === 'archived' 
                  ? 'bg-zinc-500/10 border-zinc-500/30' 
                  : 'bg-[#121214] border-zinc-800 hover:border-zinc-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-xs font-medium ${statusFilter === 'archived' ? 'text-zinc-300' : 'text-zinc-400'}`}>Archived</span>
                <Archive className={`w-4 h-4 ${statusFilter === 'archived' ? 'text-zinc-300' : 'text-zinc-600'}`} />
              </div>
              <p className={`text-2xl font-bold mt-1 ${statusFilter === 'archived' ? 'text-zinc-300' : 'text-white'}`}>
                {statusCounts.archived}
              </p>
            </button>
            
            <button
              onClick={() => { setStatusFilter("all"); setPage(0); setSelectedIds([]); }}
              className={`p-3 rounded-lg border transition-all ${
                statusFilter === 'all' 
                  ? 'bg-blue-500/10 border-blue-500/30' 
                  : 'bg-[#121214] border-zinc-800 hover:border-zinc-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-xs font-medium ${statusFilter === 'all' ? 'text-blue-400' : 'text-zinc-400'}`}>All Records</span>
                <List className={`w-4 h-4 ${statusFilter === 'all' ? 'text-blue-400' : 'text-zinc-600'}`} />
              </div>
              <p className={`text-2xl font-bold mt-1 ${statusFilter === 'all' ? 'text-blue-400' : 'text-white'}`}>
                {statusCounts.total}
              </p>
            </button>
          </div>

          {/* Filters */}
          <Card className="bg-[#121214] border-zinc-800 mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    placeholder="Search transactions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-zinc-900/50 border-zinc-800 text-white placeholder:text-zinc-600"
                    data-testid="search-input"
                  />
                </div>
                <Select value={selectedAccount} onValueChange={(v) => { setSelectedAccount(v); setPage(0); }}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white" data-testid="account-filter">
                    <SelectValue placeholder="All Accounts" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    <SelectItem value="all" className="text-white hover:bg-zinc-800">All Accounts</SelectItem>
                    {accounts.map(acc => (
                      <SelectItem key={acc.account_id} value={acc.account_id} className="text-white hover:bg-zinc-800">{acc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v); setPage(0); }}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white" data-testid="category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    <SelectItem value="all" className="text-white hover:bg-zinc-800">All Categories</SelectItem>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat} className="text-white hover:bg-zinc-800">{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); setSelectedIds([]); }}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800 text-white" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    {STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="text-white hover:bg-zinc-800">
                        <span className="flex items-center gap-2">
                          <opt.icon className="w-4 h-4" />
                          {opt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Link to="/reports">
                  <Button variant="outline" className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Actions */}
          {selectedIds.length > 0 && (
            <Card className="bg-[#121214] border-zinc-800 mb-4" data-testid="bulk-actions-bar">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <span className="text-zinc-300 text-sm">
                    {selectedIds.length} transaction(s) selected
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {statusFilter !== 'duplicate' && statusFilter !== 'archived' && (
                      <>
                        <Button
                          onClick={() => handleBulkAction('mark_duplicate')}
                          disabled={processingAction}
                          variant="outline"
                          size="sm"
                          className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                          data-testid="bulk-mark-duplicate-btn"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Mark as Duplicate
                        </Button>
                        <Button
                          onClick={() => handleBulkAction('archive')}
                          disabled={processingAction}
                          variant="outline"
                          size="sm"
                          className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                          data-testid="bulk-archive-btn"
                        >
                          <Archive className="w-4 h-4 mr-2" />
                          Archive
                        </Button>
                      </>
                    )}
                    {(statusFilter === 'duplicate' || statusFilter === 'archived' || statusFilter === 'all') && (
                      <Button
                        onClick={() => handleBulkAction('restore')}
                        disabled={processingAction}
                        variant="outline"
                        size="sm"
                        className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                        data-testid="bulk-restore-btn"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Restore
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Transactions Table */}
          <Card className="bg-[#121214] border-zinc-800">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="w-8 h-8 border-2 border-[#FCD34D] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredTransactions.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800 hover:bg-transparent">
                          <TableHead className="w-10">
                            <Checkbox
                              checked={selectedIds.length === filteredTransactions.length && filteredTransactions.length > 0}
                              onCheckedChange={handleSelectAll}
                              className="border-zinc-600"
                            />
                          </TableHead>
                          <TableHead className="text-zinc-400">Date</TableHead>
                          <TableHead className="text-zinc-400">Description</TableHead>
                          <TableHead className="text-zinc-400">Account</TableHead>
                          <TableHead className="text-zinc-400">Category</TableHead>
                          <TableHead className="text-zinc-400">Status</TableHead>
                          <TableHead className="text-zinc-400 text-right">Amount In</TableHead>
                          <TableHead className="text-zinc-400 text-right">Amount Out</TableHead>
                          <TableHead className="text-zinc-400 text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredTransactions.map((tx) => (
                          <TableRow 
                            key={tx.transaction_id} 
                            className={`border-zinc-800 table-row-hover ${(tx.is_duplicate || tx.is_archived) ? 'opacity-60' : ''}`} 
                            data-testid={`transaction-row-${tx.transaction_id}`}
                          >
                            <TableCell>
                              <Checkbox
                                checked={selectedIds.includes(tx.transaction_id)}
                                onCheckedChange={(checked) => handleSelectOne(tx.transaction_id, checked)}
                                className="border-zinc-600"
                              />
                            </TableCell>
                            <TableCell className="text-zinc-300 font-mono text-sm">{formatDate(tx.date)}</TableCell>
                            <TableCell className={`max-w-xs truncate ${(tx.is_duplicate || tx.is_archived) ? 'text-zinc-500 line-through' : 'text-white'}`}>
                              {tx.description}
                            </TableCell>
                            <TableCell className="text-zinc-400 text-sm">{getAccountName(tx.account_id)}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={`text-xs ${
                                tx.category === 'Income' ? 'badge-success' :
                                tx.is_internal_transfer ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' :
                                tx.expense_type === 'direct' ? 'badge-error' :
                                tx.expense_type === 'indirect' ? 'badge-warning' : 'badge-neutral'
                              }`}>
                                {tx.category}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {getStatusBadge(tx) || (
                                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">Active</Badge>
                              )}
                            </TableCell>
                            <TableCell className={`text-right font-mono ${(tx.is_duplicate || tx.is_archived) ? 'text-emerald-500/50' : 'text-emerald-500'}`}>
                              {tx.amount_in > 0 ? formatCurrency(tx.amount_in) : '-'}
                            </TableCell>
                            <TableCell className={`text-right font-mono ${(tx.is_duplicate || tx.is_archived) ? 'text-red-500/50' : 'text-red-500'}`}>
                              {tx.amount_out > 0 ? formatCurrency(tx.amount_out) : '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-zinc-500 hover:text-white hover:bg-zinc-800"
                                    disabled={processingAction}
                                    data-testid={`action-menu-${tx.transaction_id}`}
                                  >
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="bg-[#121214] border-zinc-800" align="end">
                                  {!tx.is_duplicate && !tx.is_archived ? (
                                    <>
                                      <DropdownMenuItem 
                                        className="text-amber-400 hover:bg-amber-500/10 cursor-pointer"
                                        onClick={() => handleMarkDuplicate(tx.transaction_id)}
                                        data-testid={`mark-duplicate-${tx.transaction_id}`}
                                      >
                                        <Copy className="w-4 h-4 mr-2" />
                                        Mark as Duplicate
                                      </DropdownMenuItem>
                                      <DropdownMenuItem 
                                        className="text-zinc-300 hover:bg-zinc-800 cursor-pointer"
                                        onClick={() => handleArchive(tx.transaction_id)}
                                        data-testid={`archive-${tx.transaction_id}`}
                                      >
                                        <Archive className="w-4 h-4 mr-2" />
                                        Archive
                                      </DropdownMenuItem>
                                    </>
                                  ) : (
                                    <DropdownMenuItem 
                                      className="text-emerald-400 hover:bg-emerald-500/10 cursor-pointer"
                                      onClick={() => handleRestore(tx.transaction_id)}
                                      data-testid={`restore-${tx.transaction_id}`}
                                    >
                                      <RotateCcw className="w-4 h-4 mr-2" />
                                      Restore
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator className="bg-zinc-800" />
                                  <DropdownMenuItem 
                                    className="text-zinc-400 hover:bg-zinc-800 cursor-pointer"
                                    onClick={() => navigate(`/review?tx=${tx.transaction_id}`)}
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    View Details
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between p-4 border-t border-zinc-800">
                    <p className="text-sm text-zinc-500">
                      Showing {page * limit + 1} - {page * limit + filteredTransactions.length} transactions
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-zinc-400">Page {page + 1}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                        onClick={() => setPage(p => p + 1)}
                        disabled={filteredTransactions.length < limit}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-16" data-testid="empty-state">
                  {statusCounts.total === 0 ? (
                    /* No transactions at all */
                    <>
                      <List className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                      <p className="text-zinc-400 font-medium">No transactions imported yet</p>
                      <p className="text-zinc-500 text-sm mt-1">Upload your first bank statement to get started</p>
                      <Link to="/upload">
                        <Button className="mt-4 bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24]">
                          Upload Statements
                        </Button>
                      </Link>
                    </>
                  ) : (
                    /* Transactions exist but filtered out */
                    <>
                      {statusFilter === 'active' ? (
                        <>
                          <EyeOff className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                          <p className="text-zinc-400 font-medium">No active transactions</p>
                          <p className="text-zinc-500 text-sm mt-1 max-w-md mx-auto">
                            All {statusCounts.total} transaction{statusCounts.total !== 1 ? 's' : ''} are currently marked as 
                            {statusCounts.duplicate > 0 && <span className="text-amber-400"> duplicate ({statusCounts.duplicate})</span>}
                            {statusCounts.duplicate > 0 && statusCounts.archived > 0 && ' or'}
                            {statusCounts.archived > 0 && <span className="text-zinc-400"> archived ({statusCounts.archived})</span>}
                          </p>
                          <div className="flex gap-3 justify-center mt-4">
                            <Button 
                              variant="outline" 
                              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                              onClick={() => { setStatusFilter("all"); setPage(0); }}
                            >
                              <List className="w-4 h-4 mr-2" />
                              View All Records
                            </Button>
                            <Link to="/upload">
                              <Button className="bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24]">
                                Upload More
                              </Button>
                            </Link>
                          </div>
                        </>
                      ) : statusFilter === 'duplicate' ? (
                        <>
                          <Copy className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                          <p className="text-zinc-400 font-medium">No duplicate transactions</p>
                          <p className="text-zinc-500 text-sm mt-1">
                            {statusCounts.active > 0 && <span>You have <span className="text-emerald-400">{statusCounts.active} active</span></span>}
                            {statusCounts.active > 0 && statusCounts.archived > 0 && ' and '}
                            {statusCounts.archived > 0 && <span><span className="text-zinc-400">{statusCounts.archived} archived</span></span>}
                            {' '}transaction{(statusCounts.active + statusCounts.archived) !== 1 ? 's' : ''}
                          </p>
                          <Button 
                            variant="outline" 
                            className="mt-4 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                            onClick={() => { setStatusFilter("active"); setPage(0); }}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Active Transactions
                          </Button>
                        </>
                      ) : statusFilter === 'archived' ? (
                        <>
                          <Archive className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                          <p className="text-zinc-400 font-medium">No archived transactions</p>
                          <p className="text-zinc-500 text-sm mt-1">
                            {statusCounts.active > 0 && <span>You have <span className="text-emerald-400">{statusCounts.active} active</span></span>}
                            {statusCounts.active > 0 && statusCounts.duplicate > 0 && ' and '}
                            {statusCounts.duplicate > 0 && <span><span className="text-amber-400">{statusCounts.duplicate} duplicate</span></span>}
                            {' '}transaction{(statusCounts.active + statusCounts.duplicate) !== 1 ? 's' : ''}
                          </p>
                          <Button 
                            variant="outline" 
                            className="mt-4 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                            onClick={() => { setStatusFilter("active"); setPage(0); }}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Active Transactions
                          </Button>
                        </>
                      ) : (
                        <>
                          <List className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                          <p className="text-zinc-500">No transactions match current filters</p>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
